import { setupTravellerAutocomplete, fillTravellerFields } from '../../lib/traveller-autocomplete';
import { setupTravellerSync } from './field-sync';
import { getSelectedOfferData, fillTravellerFromOffer } from './auto-fill';
import type { OfferDetails } from '../../lib/types';

export interface TravellerManagerConfig {
    container: HTMLElement;
    addButton: HTMLElement;
    searchUrl: string;
    getAgencyId: () => string;
    getAgencyName?: () => string;
    getOfferCache: () => Record<string, OfferDetails>;
    getSelectedOfferIds: () => string[];
    onTravellerAdded?: (card: HTMLElement) => void;
    onTravellerRemoved?: () => void;
}

export function setupTravellerManager(config: TravellerManagerConfig): void {
    const { container, addButton } = config;
    // Track autocomplete teardowns by card element for cleanup on remove
    const teardowns = new WeakMap<Element, () => void>();
    let nextIndex = 0;

    addButton.addEventListener('click', () => {
        const idx = nextIndex;
        const prefix = `Travellers[${idx}]`;
        const cdPrefix = `${prefix}.ContactDetails`;

        const card = cloneRowTemplate(idx, prefix, cdPrefix);
        container.appendChild(card);
        setupTravellerSync(prefix, cdPrefix);

        const ids = config.getSelectedOfferIds();
        if (ids.length > 0) {
            const data = getSelectedOfferData(ids, config.getOfferCache(), config.getAgencyName);
            fillTravellerFromOffer(prefix, data);
        }

        const searchInputId = card.querySelector<HTMLInputElement>('[data-role="traveller-search"]')?.id || '';
        const searchResultsId = card.querySelector<HTMLElement>('[data-role="traveller-results"]')?.id || '';
        const foreignSectionId = card.querySelector<HTMLElement>('[data-role="foreign-section"]')?.id || '';
        const searchInput = document.getElementById(searchInputId) as HTMLInputElement | null;
        const searchResults = document.getElementById(searchResultsId) as HTMLElement | null;
        if (searchInput && searchResults) {
            const teardown = setupTravellerAutocomplete(searchInput, searchResults, {
                searchUrl: config.searchUrl,
                agencyIdGetter: config.getAgencyId,
                onSelect: (t) => fillTravellerFields(t, prefix, cdPrefix, foreignSectionId),
            });
            teardowns.set(card, teardown);
        }

        nextIndex++;
        config.onTravellerAdded?.(card);
    });

    // Remove traveller (event delegation — owner row has no remove button)
    container.addEventListener('click', (e) => {
        const btn = (e.target as HTMLElement).closest('.remove-traveller-btn');
        if (!btn) return;
        const card = btn.closest('.traveller-card') as HTMLElement | null;
        if (!card) return;
        teardowns.get(card)?.();
        card.remove();
        reindex(container);
        nextIndex = container.querySelectorAll('.traveller-card').length;
        config.onTravellerRemoved?.();
    });

    // Foreign-citizen toggle (event delegation)
    container.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;
        if (!target.classList.contains('traveller-foreign-select')) return;
        const sectionId = target.getAttribute('data-target');
        if (!sectionId) return;
        const section = document.getElementById(sectionId);
        if (section) section.style.display = target.value === 'true' ? '' : 'none';
    });
}

// ── helpers ──────────────────────────────────────────────────────────────────

function cloneRowTemplate(idx: number, prefix: string, cdPrefix: string): HTMLElement {
    const template = document.getElementById('travellerRowTemplate') as HTMLTemplateElement | null;
    if (!template) throw new Error('travellerRowTemplate not found');

    const fragment = template.content.cloneNode(true) as DocumentFragment;
    const card = fragment.firstElementChild as HTMLElement;

    const collapseId = `collapseTrack_${idx}`;
    const foreignId = `foreignDoc_${idx}`;
    const searchId = `travellerSearch_${idx}`;
    const resultsId = `travellerResults_${idx}`;
    const usesTransportId = `usesTransport_${idx}`;
    const typeSelectId = `typeSelect_${idx}`;
    const foreignSelectId = `isForeign_${idx}`;

    replaceTokens(card, {
        '__PREFIX__': prefix,
        '__CDPREFIX__': cdPrefix,
        '__COLLAPSE_ID__': collapseId,
        '__FOREIGN_ID__': foreignId,
        '__SEARCH_ID__': searchId,
        '__RESULTS_ID__': resultsId,
        '__USES_TRANSPORT_ID__': usesTransportId,
        '__TYPE_SELECT_ID__': typeSelectId,
        '__FOREIGN_SELECT_ID__': foreignSelectId,
    });

    card.dataset.index = String(idx);

    // Tag elements for lookup by role instead of fragile IDs in the event handler
    const searchInput = card.querySelector<HTMLInputElement>(`#${searchId}`);
    if (searchInput) searchInput.dataset.role = 'traveller-search';
    const resultsEl = card.querySelector<HTMLElement>(`#${resultsId}`);
    if (resultsEl) resultsEl.dataset.role = 'traveller-results';
    const foreignSection = card.querySelector<HTMLElement>(`#${foreignId}`);
    if (foreignSection) foreignSection.dataset.role = 'foreign-section';

    return card;
}

function replaceTokens(root: HTMLElement, tokens: Record<string, string>): void {
    const attrs = ['name', 'id', 'for', 'data-bs-target', 'aria-controls', 'data-target', 'href'];
    root.querySelectorAll('*').forEach(el => {
        attrs.forEach(attr => {
            const val = el.getAttribute(attr);
            if (!val) return;
            let replaced = val;
            for (const [token, value] of Object.entries(tokens)) {
                replaced = replaced.replaceAll(token, value);
            }
            if (replaced !== val) el.setAttribute(attr, replaced);
        });
    });
    // Also handle the root element itself
    attrs.forEach(attr => {
        const val = root.getAttribute(attr);
        if (!val) return;
        let replaced = val;
        for (const [token, value] of Object.entries(tokens)) {
            replaced = replaced.replaceAll(token, value);
        }
        if (replaced !== val) root.setAttribute(attr, replaced);
    });
}

function reindex(container: HTMLElement): void {
    container.querySelectorAll<HTMLElement>('.traveller-card').forEach((card, newIdx) => {
        const oldPrefix = `Travellers[${card.dataset.index}]`;
        const newPrefix = `Travellers[${newIdx}]`;
        const oldCd = `${oldPrefix}.ContactDetails`;
        const newCd = `${newPrefix}.ContactDetails`;

        card.dataset.index = String(newIdx);

        card.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input, select').forEach(el => {
            const name = el.getAttribute('name');
            if (name) el.setAttribute('name', name.replace(oldPrefix, newPrefix).replace(oldCd, newCd));
        });
    });
}
